
import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  MoreVertical, 
  Play, 
  Edit, 
  Trash2, 
  Download,
  Clock,
  Calendar
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { Project } from '@/hooks/useProjects';
import { formatDistanceToNow } from 'date-fns';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const navigate = useNavigate();

  const getStatusBadge = () => {
    if (project.exported_video_storage_path) {
      return <Badge className="bg-green-600/20 text-green-400 border-green-600/30">Exported</Badge>;
    }
    if (project.export_status === 'completed') {
      return <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">Completed</Badge>;
    }
    if (project.export_status === 'processing') {
      return <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/30">Processing</Badge>;
    }
    return <Badge className="bg-gray-600/20 text-gray-400 border-gray-600/30">Draft</Badge>;
  };

  const handleEdit = () => {
    navigate(`/studio/projects/${project.id}`);
  };

  const handlePlay = () => {
    if (project.exported_video_storage_path) {
      // Handle video playback
      console.log('Play video:', project.exported_video_storage_path);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm hover:bg-white/10 transition-all duration-200 group">
      {/* Thumbnail area */}
      <div className="aspect-video bg-gray-800/50 rounded-lg mb-4 flex items-center justify-center relative overflow-hidden">
        {project.thumbnail_url ? (
          <img 
            src={project.thumbnail_url} 
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-gray-500">
            <Play className="h-8 w-8" />
          </div>
        )}
        
        {/* Play overlay for exported videos */}
        {project.exported_video_storage_path && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="icon"
              onClick={handlePlay}
              className="bg-purple-600 hover:bg-purple-700 rounded-full"
            >
              <Play className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      {/* Project info */}
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-white text-lg line-clamp-1">
              {project.title}
            </h3>
            {project.description && (
              <p className="text-gray-400 text-sm line-clamp-2 mt-1">
                {project.description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>

        {/* Status and metadata */}
        <div className="flex items-center justify-between">
          {getStatusBadge()}
          <div className="text-xs text-gray-400 flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {formatDistanceToNow(new Date(project.updated_at), { addSuffix: true })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          
          {project.exported_video_storage_path && (
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
